import React, { useState, useContext, Suspense } from "react";
import { Outlet, useLocation } from "react-router";
import Header from "../../helpers/components/header";
import {
    PricingDisplayMethodContext,
    ErrorContext,
    LoadingContext,
    DebugModeContext,
} from "/src/helpers/contexts/agentContext.jsx";
import { ThreeDot } from "react-loading-indicators";

import { useLocalStorage, useSessionStorage } from "@uidotdev/usehooks";
import { SUPPORT_PHONE } from "/src/config/constants.js";

export default function SiteWrapper() {
    const [defaultSearch, setDefaultSearch] = useState();
    const [currentError, setCurrentError] = useState();
    const [currentLoading, setCurrentLoading] = useState(false);
    const [currentLoadingMessage, setCurrentLoadingMessage] = useState("");

    const [isDebug] = useContext(DebugModeContext);
    const [pricingDisplayMethod, setPricingDisplayMethod] = useLocalStorage(
        "pricingDisplayMethod",
        "pp"
    );

    const location = useLocation();
    const hideSearch = location.pathname.toLowerCase().startsWith("/checkout/");

    const setLoading = (isLoading, message) => {
        setCurrentLoading(isLoading);
        setCurrentLoadingMessage(message);
    };

    // No-availability failures (the flight/property searches finding nothing — most commonly a
    // reopened quote whose holiday has since sold out) get friendly copy and a route back to
    // search, rather than the generic "something has gone wrong" fault message.
    const noAvailability =
        typeof currentError === "string" &&
        (/^(Flight not found|Property not found)/i.test(currentError) ||
            /no longer available/i.test(currentError));
    const fromReopenedQuote =
        noAvailability && window.location.search.includes("reopened=quote");
    return (
        <PricingDisplayMethodContext value={[pricingDisplayMethod, setPricingDisplayMethod]}>
            <ErrorContext value={[currentError, setCurrentError]}>
                <LoadingContext value={[currentLoading, setLoading]}>
                    <div className="container p-6 mx-auto">
                        {currentError && (
                            <div
                                className="fixed inset-0 bg-black/50 flex items-center justify-center z-999 "
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="error-modal-title"
                            >
                                <div className="bg-white rounded-[24px] p-8 max-w-sm mx-4 text-center shadow-2xl relative">
                                    <button
                                        type="button"
                                        aria-label="Close"
                                        onClick={(e) => setCurrentError(null)}
                                        className="p-2 text-gray-500 hover:text-gray-700 absolute right-0 mr-3"
                                    >
                                        <svg
                                            className="w-6 h-6"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                    <div className="flex justify-center">
                                        <div className="relative text-emp_dark">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="128"
                                                height="128"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                                                <path d="M9 10l.01 0" />
                                                <path d="M15 10l.01 0" />
                                                <path d="M9.5 15.25a3.5 3.5 0 0 1 5 0" />
                                            </svg>
                                        </div>
                                    </div>

                                    <h2
                                        id="error-modal-title"
                                        className="text-[32px] font-semibold text-emp mb-2"
                                    >
                                        {noAvailability ? "Sorry!" : "Oh No!"}
                                    </h2>

                                    {noAvailability ? (
                                        <>
                                            <p className="text-content_2 text-lg">
                                                {fromReopenedQuote
                                                    ? "This quote is no longer available — please try a new search."
                                                    : "This holiday is no longer available — please try a new search."}
                                            </p>
                                            <a
                                                href="https://www.olympicholidays.com/"
                                                className="btn-primary btn--medium inline-flex mt-4"
                                            >
                                                <span>Start a new search</span>
                                            </a>
                                            <p className="text-content_2 text-sm mt-3">
                                                Or call us on <strong>{SUPPORT_PHONE}</strong> and
                                                we'll find you something similar.
                                            </p>
                                        </>
                                    ) : (
                                        <p className="text-content_2 text-lg">
                                            Sorry something has gone wrong at our end. Please phone
                                            our team on <strong>{SUPPORT_PHONE}</strong>
                                        </p>
                                    )}
                                    {isDebug && (
                                        <pre className="text-left whitespace-pre-wrap">
                                            {currentError}
                                        </pre>
                                    )}
                                </div>
                            </div>
                        )}

                        {currentLoading && (
                            <div
                                className="fixed inset-0 bg-black/50 flex items-center justify-center z-999 "
                                role="status"
                                aria-live="polite"
                            >
                                <div className="bg-white rounded-[24px] p-8 max-w-sm mx-4 text-center shadow-2xl relative">
                                    <div className="flex justify-center">
                                        <div className="relative text-emp_dark">
                                            <ThreeDot
                                                color="#009DD6"
                                                size="large"
                                                text="Loading"
                                                textColor="#009DD6"
                                                easing="ease-in-out"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-content_2 text-lg">
                                        {currentLoadingMessage}
                                    </p>
                                </div>
                            </div>
                        )}

                        <Header defaultSearch={defaultSearch} hideSearch={hideSearch} />
                        <Suspense
                            fallback={
                                <div className="flex justify-center py-20">
                                    <ThreeDot
                                        color="#009DD6"
                                        size="large"
                                        text="Loading"
                                        textColor="#009DD6"
                                        easing="ease-in-out"
                                    />
                                </div>
                            }
                        >
                            <Outlet context={{ defaultSearch, setDefaultSearch }} />
                        </Suspense>
                    </div>
                </LoadingContext>
            </ErrorContext>
        </PricingDisplayMethodContext>
    );
}
